import { oc } from "@orpc/contract";
import { z } from "zod";

const UUIDSchema = z.uuid();
const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  error: "Must be a numeric string",
});

const InvoicePreviewSchema = z.object({
  id: UUIDSchema,
  invoiceNumber: z.string().nullable().optional(),
  type: z.string().optional(),
  supplierId: UUIDSchema.optional(),
});

const PaymentInvoicePreviewSchema = InvoicePreviewSchema.extend({
  amount: DecimalStringSchema,
});

const FinancialTransactionPreviewSchema = z.object({
  id: UUIDSchema,
  transactionDate: z.string(),
  amount: DecimalStringSchema,
  direction: z.string(),
  merchantName: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
});

export const PaymentSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  invoiceId: UUIDSchema.nullable().optional(),
  financialTransactionId: UUIDSchema.nullable().optional(),
  amount: DecimalStringSchema,
  currency: z.string(),
  paymentDate: z.string(),
  method: z.string(),
  status: z.string(),
  referenceNumber: z.string().nullable().optional(),
  memo: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().optional(),
  invoices: z.array(PaymentInvoicePreviewSchema).optional(),
  invoice: InvoicePreviewSchema.nullable().optional(),
  financialTransaction: FinancialTransactionPreviewSchema.nullable().optional(),
});

export const CreatePaymentInputSchema = z.object({
  amount: DecimalStringSchema,
  currency: z.string().optional(),
  paymentDate: z.string(),
  method: z.string(),
  invoiceId: UUIDSchema.optional(),
  financialTransactionId: UUIDSchema.optional(),
  referenceNumber: z.string().optional(),
  memo: z.string().optional(),
});

export const ListPaymentsInputSchema = z.object({
  invoiceId: UUIDSchema.optional(),
  status: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  payRunDate: z.string().optional(),
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

export const ListPaymentsResponseSchema = z.object({
  payments: z.array(PaymentSchema),
  total: z.int().nonnegative(),
});

export const UpdatePaymentInputSchema = z.object({
  id: UUIDSchema,
  data: z
    .object({
      amount: DecimalStringSchema.optional(),
      currency: z.string().optional(),
      paymentDate: z.string().optional(),
      method: z.string().optional(),
      status: z.string().optional(),
      invoiceId: UUIDSchema.optional(),
      financialTransactionId: UUIDSchema.optional(),
      referenceNumber: z.string().optional(),
      memo: z.string().optional(),
    })
    .refine(
      (data) => Object.keys(data).length > 0,
      "At least one field must be provided",
    ),
});

export const ApprovePaymentInputSchema = z.object({
  id: UUIDSchema,
});

export const ApprovePaymentResponseSchema = PaymentSchema;

export const ApprovePaymentsInputSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("payRun"),
    payRunDate: z.string(), // YYYY-MM-DD format
    excludedPaymentIds: z.array(UUIDSchema).optional(),
  }),
  z.object({
    type: z.literal("paymentIds"),
    paymentIds: z.array(UUIDSchema).min(1),
  }),
]);

export const ApprovePaymentsResponseSchema = z.object({
  approvedCount: z.int().nonnegative(),
  approvedPaymentIds: z.array(UUIDSchema),
});

export const paymentsContracts = {
  create: oc
    .input(CreatePaymentInputSchema)
    .output(PaymentSchema)
    .route({ method: "POST" }),
  get: oc
    .input(z.object({ id: UUIDSchema }))
    .output(PaymentSchema.nullable())
    .route({ method: "GET" }),
  list: oc
    .input(ListPaymentsInputSchema)
    .output(ListPaymentsResponseSchema)
    .route({ method: "GET" }),
  update: oc
    .input(UpdatePaymentInputSchema)
    .output(PaymentSchema.nullable())
    .route({ method: "PUT" }),
  approve: oc
    .input(ApprovePaymentInputSchema)
    .output(ApprovePaymentResponseSchema)
    .route({ method: "POST" }),
  approveMultiple: oc
    .input(ApprovePaymentsInputSchema)
    .output(ApprovePaymentsResponseSchema)
    .route({ method: "POST" }),
} as const;

export type Payment = z.infer<typeof PaymentSchema>;
export type CreatePaymentInput = z.infer<typeof CreatePaymentInputSchema>;
export type ListPaymentsInput = z.infer<typeof ListPaymentsInputSchema>;
export type ListPaymentsResponse = z.infer<typeof ListPaymentsResponseSchema>;
export type UpdatePaymentInput = z.infer<typeof UpdatePaymentInputSchema>;
export type ApprovePaymentInput = z.infer<typeof ApprovePaymentInputSchema>;
export type ApprovePaymentResponse = z.infer<
  typeof ApprovePaymentResponseSchema
>;

export type ApprovePaymentsInput = z.infer<typeof ApprovePaymentsInputSchema>;
export type ApprovePaymentsResponse = z.infer<
  typeof ApprovePaymentsResponseSchema
>;
